import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { ErrorBanner } from 'ui';

import { useGetMemberDataExports } from '../../hooks/useGetMemberDataExports';
import { useInitMemberDataExport } from '../../hooks/useInitMemberDataExport';

const Wrapper = styled.div`
  display: grid;
  gap: 20px;
  margin: 0 0 1.5rem;
  scroll-margin-top: 1.5rem;
`;

export const ErrorMessages = () => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const [isDownloadError, setIsDownloadError] = useState(false);
  const { isError: fetchError } = useGetMemberDataExports();
  const { isError: initError } = useInitMemberDataExport();
  const queryClient = useQueryClient();

  // Chekc if any of the queries for the download redirect have failed
  // and set the error state accordingly
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const redirectWithError = queryClient
        .getQueryCache()
        .findAll({ queryKey: ['useGetMemberDataExportRedirect'] })
        .some((query) => query.state.status === 'error');

      setIsDownloadError(redirectWithError);
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  // Scroll to error messages when they appear if necessary
  useEffect(() => {
    if (ref.current && (fetchError || initError || isDownloadError)) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [fetchError, initError, isDownloadError]);

  const errorMessages = [
    fetchError && intl.formatMessage({ id: 'membership_hub_data_export_requests_fetch_failed' }),
    initError && intl.formatMessage({ id: 'membership_hub_data_export_request_failed' }),
    isDownloadError && intl.formatMessage({ id: 'membership_hub_data_export_download_failed' }),
  ].filter((error) => typeof error === 'string');

  if (errorMessages.length === 0) {
    return null;
  }

  return (
    <Wrapper ref={ref}>
      {errorMessages.map((error, index) => (
        <ErrorBanner key={index} aria-live="assertive" aria-atomic="true">
          {error}
        </ErrorBanner>
      ))}
    </Wrapper>
  );
};
